import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {OwnerElement, OwnerLoaderService} from '../service/owner-loader.service';

interface OwnerElementStore {
  byId: { [key: number]: OwnerElement }
  byParent: { [key: number]: OwnerElement[] }
}

interface OwnerElementStoreCollection {
  [key: number]: OwnerElementStore
}

@Injectable({
  providedIn: 'root'
})
export class OwnerTreeDataSourceService {
  ownerLoaderService = inject(OwnerLoaderService)

  private readonly collection: OwnerElementStoreCollection = {};

  initializeStore(root: OwnerElement) {
    const newRoot = {
      ...root,
      parent: 0
    };

    this.collection[root.id] = {
      byParent: {0: [newRoot]},
      byId: {[newRoot.id]: newRoot}
    }
  }

  private getStore(rootId: number): OwnerElementStore {
    return this.collection[rootId] ?? null;
  }

  getParent(rootId: number, node: OwnerElement): OwnerElement {
    if (node.parent === 0) { // root
      return null;
    }

    return this.getStore(rootId).byId[node.parent];
  }

  getNodeById(rootId: number, nodeId: number) {
    return this.getStore(rootId).byId[nodeId];
  }

  loadChildren(rootId: number, node: OwnerElement, parentExpanded: boolean): Observable<OwnerElement[]> {
    if (!parentExpanded || !node.extraData.hasChildren) {
      console.debug(`Not expanded or no children for ${node.label} (${node.id})`);
      return of([]);
    }

    const initialStore = this.getStore(rootId);
    if(!initialStore) {
      throw new Error('No initialized store?');
    }

    const flattenedStoreChildren = initialStore.byParent[node.id] ?? null;
    if (flattenedStoreChildren && flattenedStoreChildren.every(child => !child.extraData.hasChildren || !!initialStore.byParent[child.id])) {
      return of(flattenedStoreChildren);
    }

    console.debug(`Loading children for ${node.label} (${node.id})`);

    return this.ownerLoaderService.loadChildren(node).pipe(tap(result => {
      const store = this.getStore(rootId);
      store.byParent[node.id] = result;
      result.forEach(child => store.byId[child.id] = child);
      console.debug(`New store after loading ${node.label} (${node.id}): ${JSON.stringify(store, null, 2)}`);
    }), map(() => {
      const newResult = this.getStore(rootId).byParent[node.id];
      console.debug(`Returning value: ${JSON.stringify(newResult, null, 2)}`);
      return newResult;
    }));
  }
}
